<template>
  <button :type="type" :class="buttonClasses" :disabled="disabled">
    <component
      :is="icon"
      v-if="icon && iconPosition === 'start'"
      :class="iconColor"
      aria-hidden="true"
    />
    <slot />
    <component
      :is="icon"
      v-if="icon && iconPosition === 'end'"
      :class="iconColor"
      aria-hidden="true"
    />
  </button>
</template>

<script setup lang="ts">
import { computed, type Component } from "vue";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "danger"
  | "primary-outline"
  | "success"
  | "warning"
  | "info"
  | "light"
  | "dark"
  | "link";

export type ButtonSize = "sm" | "md";

interface Props {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  icon?: Component;
  iconPosition?: "start" | "end";
  iconColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "secondary",
  size: "md",
  disabled: false,
  type: "button",
  icon: undefined,
  iconPosition: "start",
  iconColor: "currentColor",
});

const buttonClasses = computed(() => {
  const baseStyles = [
    "inline-flex",
    "items-center",
    "justify-center",
    "gap-2",
    "box-border",
    "border",
    "rounded-md",
    "font-medium",
    "whitespace-nowrap",
    "transition-colors",
    "duration-150",
    "focus:outline-none",
    "focus-visible:ring-2",
    "focus-visible:ring-accent/40",
    "dark:focus-visible:ring-accent-dark/40",
    "disabled:opacity-50",
    "disabled:cursor-not-allowed",
  ];

  const sizeStyles = {
    sm: ["h-8", "px-3", "text-xs"],
    md: ["h-10", "px-4", "text-sm"],
  };

  const variantStyles = {
    // Solid accent fill (same purple in both themes: white label stays
    // ≥4.5:1; the lighter accent-dark tone is for text/icons only).
    primary: [
      "bg-accent",
      "border-transparent",
      "text-white",
      "hover:bg-accent-hover",
    ],
    "primary-outline": [
      "bg-transparent",
      "border-accent/50",
      "text-accent",
      "dark:text-accent-dark",
      "hover:bg-accent-soft",
      "dark:hover:bg-accent-soft-dark",
      "hover:border-accent",
    ],
    secondary: [
      "bg-box-container-bg",
      "dark:bg-input-bg-dark",
      "border-container-border",
      "dark:border-container-border-dark",
      "text-heading",
      "dark:text-heading-dark",
      "shadow-card",
      "hover:bg-secondary-bg",
      "dark:hover:bg-secondary-bg-dark",
    ],
    // Solid destructive: danger-strong keeps the white label ≥4.5:1.
    danger: [
      "bg-danger-strong",
      "dark:bg-danger-strong-dark",
      "border-transparent",
      "text-white",
      "hover:bg-danger-strong/90",
      "dark:hover:bg-danger-strong-dark/90",
    ],
    success: [
      "bg-status-safe",
      "dark:bg-status-safe-dark",
      "border-status-safe-border/70",
      "text-status-safe-icon",
      "dark:text-status-safe-border",
      "hover:bg-status-safe-border/30",
    ],
    warning: [
      "bg-status-analyzed",
      "dark:bg-status-analyzed-dark",
      "border-status-analyzed-border",
      "text-status-analyzed-icon",
      "dark:text-status-analyzed-border",
      "hover:bg-status-analyzed-border/30",
    ],
    info: [
      "bg-status-completed",
      "dark:bg-status-completed-dark",
      "border-status-completed-border/60",
      "text-status-completed-icon",
      "dark:text-status-completed-border",
      "hover:bg-status-completed-border/20",
    ],
    light: [
      "bg-secondary-bg",
      "dark:bg-secondary-bg-dark",
      "border-transparent",
      "text-heading",
      "dark:text-heading-dark",
      "hover:bg-container-border/60",
      "dark:hover:bg-container-border-dark/60",
    ],
    dark: [
      "bg-heading",
      "dark:bg-heading-dark",
      "border-transparent",
      "text-white",
      "dark:text-page-bg-dark",
      "hover:bg-heading/90",
      "dark:hover:bg-heading-dark/90",
    ],
    link: [
      "bg-transparent",
      "border-transparent",
      "px-1",
      "text-accent",
      "dark:text-accent-dark",
      "hover:underline",
      "underline-offset-4",
    ],
  };

  return [
    ...baseStyles,
    ...(sizeStyles[props.size] ?? sizeStyles.md),
    ...variantStyles[props.variant],
  ];
});
</script>
